import { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { fmtNum } from "@/lib/constants";

export default function LocalChargesPage() {
  const [charges, setCharges] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineId, setLineId] = useState("all");
  const [stype, setStype] = useState("export");

  useEffect(() => {
    api.get("/shipping-lines").then(({ data }) => setLines(data));
  }, []);
  useEffect(() => {
    const q = new URLSearchParams({ shipment_type: stype });
    if (lineId !== "all") q.set("shipping_line_id", lineId);
    api.get(`/local-charges?${q.toString()}`).then(({ data }) => setCharges(data));
  }, [lineId, stype]);

  const grouped = useMemo(() => {
    const g = {};
    charges.forEach((c) => {
      const key = c.shipping_line_name || "General";
      (g[key] = g[key] || []).push(c);
    });
    return g;
  }, [charges]);

  return (
    <Layout>
      <div className="bg-navy text-white py-14">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">Shipping Line Local Charges</h1>
          <p className="text-white/60 mt-2 max-w-2xl">Ocean freight and local charges are maintained separately. Below are the applicable local charges per shipping line.</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-3 mb-8">
          <Select value={stype} onValueChange={setStype}>
            <SelectTrigger className="w-40 rounded-sm" data-testid="lc-stype"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="export">Export Charges</SelectItem><SelectItem value="import">Import Charges</SelectItem></SelectContent>
          </Select>
          <Select value={lineId} onValueChange={setLineId}>
            <SelectTrigger className="w-56 rounded-sm" data-testid="lc-line"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Shipping Lines</SelectItem>{lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {Object.keys(grouped).length === 0 && <p className="text-muted-foreground">No charges found.</p>}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(grouped).map(([line, items]) => (
            <div key={line} className="border border-border rounded-md overflow-hidden bg-card" data-testid={`lc-group-${line}`}>
              <div className="bg-secondary px-5 py-3 flex items-center justify-between">
                <span className="font-heading font-bold text-navy">{line}</span>
                <Badge variant="outline" className="rounded-sm capitalize">{stype}</Badge>
              </div>
              <div>
                {items.map((c) => (
                  <div key={c.id} className="flex justify-between items-center px-5 py-3 border-b border-border last:border-0 text-sm">
                    <div>
                      <div className="text-navy font-medium">{c.charge_type}</div>
                      <div className="text-xs text-muted-foreground">{c.port_name} · {c.tax_percent ? `+${c.tax_percent}% GST` : "No tax"}</div>
                    </div>
                    <div className="font-mono-data font-semibold text-navy">{c.currency} {fmtNum(c.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
